import { useState } from "react";
import { MapPin, Phone, MessageCircle, Mail, Clock } from "lucide-react";
import { PERAHAN_INFO } from "@/lib/constants";
import { useToast } from "@/hooks/use-toast";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-[var(--perahan-ivory)] px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl font-serif font-bold text-[var(--perahan-charcoal)] mb-6">
            Visit Our Store
          </h1>
          <p className="text-xl text-[var(--perahan-gray)] max-w-3xl mx-auto">
            Experience our heritage collection in person at our boutique in DHA, Karachi
          </p>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-serif font-bold text-[var(--perahan-charcoal)] mb-8">
                Get in Touch
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="text-[var(--perahan-maroon)] mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-[var(--perahan-charcoal)] mb-1">Address</h3>
                    <p className="text-[var(--perahan-gray)]">{PERAHAN_INFO.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Phone className="text-[var(--perahan-maroon)] mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-[var(--perahan-charcoal)] mb-1">Phone</h3>
                    <p className="text-[var(--perahan-gray)]">{PERAHAN_INFO.phone}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <MessageCircle className="text-green-500 mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-[var(--perahan-charcoal)] mb-1">WhatsApp</h3>
                    <p className="text-[var(--perahan-gray)]">{PERAHAN_INFO.whatsapp}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="text-[var(--perahan-maroon)] mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-[var(--perahan-charcoal)] mb-1">Email</h3>
                    <p className="text-[var(--perahan-gray)]">{PERAHAN_INFO.email}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Clock className="text-[var(--perahan-maroon)] mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-[var(--perahan-charcoal)] mb-1">Store Hours</h3>
                    <div className="text-[var(--perahan-gray)]">
                      <p>Monday - Saturday: 10:00 AM - 8:00 PM</p>
                      <p>Sunday: 2:00 PM - 7:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <button 
                  onClick={() => {
                    const message = encodeURIComponent("Hello! I'd like to visit your store or inquire about your collection. Could you please provide more information?");
                    window.open(`https://wa.me/${PERAHAN_INFO.whatsapp.replace(/\D/g, '')}?text=${message}`, '_blank');
                  }}
                  className="btn-terracotta flex items-center space-x-2"
                >
                  <MessageCircle size={16} />
                  <span>Chat with us on WhatsApp</span>
                </button>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-serif font-bold text-[var(--perahan-charcoal)] mb-8">
                Send us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-[var(--perahan-charcoal)] font-medium mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--perahan-maroon)] focus:border-[var(--perahan-maroon)] outline-none transition-colors"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-[var(--perahan-charcoal)] font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--perahan-maroon)] focus:border-[var(--perahan-maroon)] outline-none transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-[var(--perahan-charcoal)] font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--perahan-maroon)] focus:border-[var(--perahan-maroon)] outline-none transition-colors resize-vertical"
                    placeholder="Tell us about your inquiry, custom order requests, or any questions you have..."
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-[var(--perahan-ivory)] px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-[var(--perahan-charcoal)] mb-4">
              Find Us
            </h2>
            <p className="text-lg text-[var(--perahan-gray)]">
              Located in the heart of DHA Phase IV, Karachi
            </p>
          </div>

          {/* Google Maps Embed */}
          <div className="rounded-lg overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3620.2444444444444!2d67.0261111!3d24.8607!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDUxJzM4LjUiTiA2N8KwMDEnMzQiRQ!5e0!3m2!1sen!2s!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Perahan Store Location - DHA Phase IV, Karachi"
            />
          </div>

          <div className="mt-8 text-center">
            <p className="text-[var(--perahan-gray)] mb-4">
              Can't find us? Give us a call or send a WhatsApp message for directions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`tel:${PERAHAN_INFO.phone}`}
                className="btn-primary inline-flex items-center justify-center space-x-2"
              >
                <Phone size={16} />
                <span>Call Now</span>
              </a>
              <button 
                onClick={() => {
                  const message = encodeURIComponent("Hi! I need directions to your store. Could you help me find the exact location?");
                  window.open(`https://wa.me/${PERAHAN_INFO.whatsapp.replace(/\D/g, '')}?text=${message}`, '_blank');
                }}
                className="btn-terracotta inline-flex items-center justify-center space-x-2"
              >
                <MessageCircle size={16} />
                <span>Get Directions</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
